import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  AwardCode, AwardReference, AwardReviewList, AwardRuleList, AwardSettings, AwardSubjectKind,
  StaffAwardPerson, StaffAwardProfile,
} from '../types/awards';

/**
 * Award classifications and pay review reminders, `/api/awards`. Every call
 * needs a Timesheet HR seat. Date of birth goes nowhere else.
 */
class AwardService {
  reference(): Promise<ApiResponse<AwardReference>> {
    return apiService.get('/awards/reference');
  }

  settings(): Promise<ApiResponse<AwardSettings>> {
    return apiService.get('/awards/settings');
  }

  saveSettings(body: AwardSettings): Promise<ApiResponse<AwardSettings>> {
    return apiService.put('/awards/settings', body);
  }

  person(userId: number): Promise<ApiResponse<StaffAwardPerson>> {
    return apiService.get(`/awards/people/${userId}`);
  }

  saveProfile(userId: number, body: Omit<StaffAwardProfile, 'updated_at' | 'updated_by_name'>): Promise<ApiResponse<StaffAwardPerson>> {
    return apiService.put(`/awards/people/${userId}/profile`, body);
  }

  classify(userId: number, body: {
    award_code: AwardCode; level_key: string; effective_from: string; reason?: string | null; prompt_id?: number | null;
  }): Promise<ApiResponse<StaffAwardPerson>> {
    return apiService.post(`/awards/people/${userId}/classifications`, body);
  }

  reviews(): Promise<ApiResponse<AwardReviewList>> {
    return apiService.get('/awards/reviews');
  }

  resolve(id: number, outcome: 'no_change' | 'dismissed', note?: string): Promise<ApiResponse<AwardReviewList>> {
    return apiService.post(`/awards/reviews/${id}/resolve`, { outcome, note: note || null });
  }

  /** Raises everything due now, without notifying anyone. */
  checkNow(): Promise<ApiResponse<AwardReviewList>> {
    return apiService.post('/awards/reviews/check', {});
  }

  rules(): Promise<ApiResponse<AwardRuleList>> {
    return apiService.get('/awards/rules');
  }

  addRule(body: { award_code: AwardCode; level_key: string; subject_kind: AwardSubjectKind; subject_ref: string | null }): Promise<ApiResponse<AwardRuleList>> {
    return apiService.post('/awards/rules', body);
  }

  deleteRule(id: number): Promise<ApiResponse<AwardRuleList>> {
    return apiService.delete(`/awards/rules/${id}`);
  }

  addSuggested(award_code: AwardCode): Promise<ApiResponse<AwardRuleList>> {
    return apiService.post('/awards/rules/suggested', { award_code });
  }
}

export default new AwardService();
